import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import { AlbumCellProps, LoadingSpinner } from '../../components/Molecules';
import { RecentAlbumListTemplate } from '../../components/Templates';
import { SpacePageType } from '../../constants/spacePageType';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import { SpaceListType } from '../../models/SpaceStore';
import { WebBoosterType } from '../../models/UserStore';
import { useRecordStore } from '../../stores/RecordStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';

const { fetchHotSpaces } = useSpaceStore.getState();

interface ClubsRecentAlbumListProps {
  spacePageType: SpacePageType;
}

const ClubsRecentAlbumList: React.FC<ClubsRecentAlbumListProps> = ({
  spacePageType,
}) => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);

  const activeUserId = useSessionStore((store) => store.activeUserId);

  const userAccount = useUserStore((store) =>
    store.getAccount(activeUserId ?? ''),
  );

  useRedirectEntry();

  // MARK: Get Recent Classroom
  const recentSpaceField = useSpaceStoreListField(
    'spaces',
    SpaceListType.Recent,
  );

  const webBoosterType = userAccount?.settings.webBoosterType;
  const fetchRecentSpaces = useCallback<FetchListFn>(
    (cursor) =>
      useSpaceStore.getState().fetchRecentSpaces(webBoosterType, cursor),
    [webBoosterType],
  );

  const { fetchAll: fetchAllRecentSpaces } = useFetchList(
    recentSpaceField,
    fetchRecentSpaces,
  );

  const loadSpaces = useCallback(async () => {
    switch (spacePageType) {
      case SpacePageType.Hot:
        await fetchHotSpaces({
          label:
            userAccount?.settings.webBoosterType === WebBoosterType.Normal
              ? 'trending'
              : 'learning',
          state: 'available',
        });
        break;
      case SpacePageType.Recent:
        await fetchAllRecentSpaces();
        break;
    }
  }, [
    fetchAllRecentSpaces,
    spacePageType,
    userAccount?.settings.webBoosterType,
  ]);

  useEffect(() => {
    loadSpaces().catch(console.error);
  }, [loadSpaces]);

  const hotsSpaces = useSpaceStore((store) => {
    return store.getSpaces(SpaceListType.Hot);
  }, arrayEqual(shallow));

  const recentSpaces = useSpaceStore((store) => {
    return store.getSpaces(SpaceListType.Recent);
  }, arrayEqual(shallow));

  const spaces = useMemo(() => {
    switch (spacePageType) {
      case SpacePageType.Hot:
        return hotsSpaces;
      case SpacePageType.Recent:
        return recentSpaces.filter((item) => item.classroomId !== activeUserId); // designer say no include own club
    }
  }, [spacePageType, hotsSpaces, recentSpaces, activeUserId]);

  // MARK: Club Recent Albums
  const onClickAlbum = useCallback(
    (gameSetId: string, classroomId: string) => {
      navigate(`/club/${classroomId}/journey/${gameSetId}`, {
        state: { withBackState: true },
      });
    },
    [navigate],
  );

  const recentAlbumFieldName = `${spacePageType}_recent`;
  const recentAlbumField = useSpaceStoreListField(
    'albums',
    recentAlbumFieldName,
  );

  const fetchRecentAlbums = useCallback<FetchListFn>(
    (cursor) =>
      useSpaceStore.getState().fetchRecentAlbums(
        {
          fieldName: recentAlbumFieldName,
          withAlbumSummaries: true,
        },
        cursor,
      ),
    [recentAlbumFieldName],
  );

  const {
    fetchMore: fetchMoreAlbums,
    refresh: refreshClubRecentAlbums,
    hasCursor,
  } = useFetchList(recentAlbumField, fetchRecentAlbums);

  useEffect(() => {
    if (activeUserId) {
      setIsLoading(true);
      refreshClubRecentAlbums()
        .catch(console.error)
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [activeUserId, refreshClubRecentAlbums]);

  const loadMoreItems = useCallback(() => {
    fetchMoreAlbums().catch(console.error);
  }, [fetchMoreAlbums]);

  const unFilteredAlbums = useSpaceStore((spaceStoreState) => {
    return spaceStoreState.getAlbums(recentAlbumFieldName);
  }, arrayEqual(shallow));

  const albums = useMemo(() => {
    return unFilteredAlbums.filter((album) =>
      spaces.some((space) => space.classroomId === album.classroomId),
    );
  }, [spaces, unFilteredAlbums]);

  const albumSummaries = useRecordStore((store) => {
    const gameSetIds = albums.map((item) => item.gameSetId);

    return gameSetIds.map((gameSetId) =>
      store.getAlbumSummary(gameSetId, activeUserId ?? ''),
    );
  }, arrayEqual(shallow));

  const albumData = useMemo<AlbumCellProps[]>(() => {
    return albums.map((album) => {
      const albumSummary = albumSummaries.find(
        (item) => item?.gameSetId === album.gameSetId,
      );
      const space = spaces.find(
        (item) => item.classroomId === album.classroomId,
      );
      return {
        album: album,
        albumClassroom: space,
        albumSummary: albumSummary,
        onClick: () => {
          onClickAlbum(album.gameSetId, album.classroomId);
        },
      };
    });
  }, [albums, albumSummaries, spaces, onClickAlbum]);

  return isLoading ? (
    <LoadingSpinner loading={isLoading} />
  ) : (
    <RecentAlbumListTemplate
      albumData={albumData}
      hasCursor={hasCursor}
      loadMoreItems={loadMoreItems}
    />
  );
};

export default ClubsRecentAlbumList;
